import { useState, useEffect, useCallback } from 'react';
import { SavChat, SavMessage, SenderType, ChatReason } from '@/models/savChat';
import { SavChatRepository } from '@/services/firebase/sav-chat-repository';
import { useAuth, useCustomer } from '@/app/providers';

/**
 * Hook pour gérer un chat SAV individuel
 */
/**
 * Hook pour gérer un chat SAV individuel
 */
export const useSavChat = (chatId: string | null, forcedSenderType?: SenderType) => {
  const { currentUser } = useAuth();
  const { customerId } = useCustomer();
  const [chat, setChat] = useState<SavChat | null>(null);
  const [messages, setMessages] = useState<SavMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Écouter les changements du chat
  useEffect(() => {
    if (!chatId) {
      setChat(null);
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribeChat = SavChatRepository.subscribeToChat(chatId, (updatedChat) => {
      setChat(updatedChat);
      setLoading(false);
    });

    const unsubscribeMessages = SavChatRepository.subscribeToMessages(
      chatId,
      (updatedMessages) => {
        setMessages(updatedMessages);
      },
      50
    );

    return () => {
      unsubscribeChat();
      unsubscribeMessages();
    };
  }, [chatId]);

  /**
   * Envoyer un message
   */
  const sendMessage = useCallback(
    async (text: string) => {
      if (!chatId || !currentUser || !text.trim()) {
        return;
      }

      try {
        // Récupérer le chat actuel depuis l'état sans dépendance
        const currentChat = chat;

        // Déterminer le type d'expéditeur
        let isCustomer = false;
        let senderType = SenderType.ADMIN;

        if (forcedSenderType) {
          senderType = forcedSenderType;
          isCustomer = forcedSenderType === SenderType.CUSTOMER;
        } else {
          isCustomer = !!(customerId && currentChat?.customerId === customerId);
          senderType = isCustomer ? SenderType.CUSTOMER : SenderType.ADMIN;
        }

        const senderId = isCustomer ? (customerId || currentUser.uid) : currentUser.uid;
        const senderName = isCustomer
          ? currentChat?.customerName || 'Client'
          : currentUser.displayName || currentUser.email || 'Admin';

        await SavChatRepository.addMessage(chatId, {
          senderType,
          senderId,
          senderName,
          message: text.trim(),
        });
      } catch (err) {
        console.error('❌ Erreur lors de l\'envoi du message:', err);
        setError('Erreur lors de l\'envoi du message');
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chatId, currentUser, customerId]
  );

  /**
   * Marquer les messages comme lus
   */
  const markAsRead = useCallback(async () => {
    if (!chatId) return;

    try {
      // Récupérer le chat actuel depuis l'état sans dépendance
      const currentChat = chat;

      // Déterminer si c'est un admin ou un client
      let isCustomer = false;

      if (forcedSenderType) {
        isCustomer = forcedSenderType === SenderType.CUSTOMER;
      } else {
        isCustomer = !!(customerId && currentChat?.customerId === customerId);
      }

      const readerType = isCustomer ? 'customer' : 'admin';

      await SavChatRepository.markMessagesAsRead(chatId, readerType);
    } catch (err) {
      console.error('❌ Erreur lors du marquage des messages comme lus:', err);
    }
  }, [chatId, customerId, forcedSenderType]); // Removed 'chat' from dependencies

  /**
   * Réouvrir un chat
   */
  const reopenChat = useCallback(
    async (reason: ChatReason, reasonDetails: string) => {
      if (!chatId) return;
      try {
        await SavChatRepository.reopenChat(chatId, reason, reasonDetails);
      } catch (err) {
        console.error('❌ Erreur lors de la réouverture du chat:', err);
        setError('Erreur lors de la réouverture');
        throw err;
      }
    },
    [chatId]
  );

  return {
    chat,
    messages,
    loading,
    error,
    sendMessage,
    markAsRead,
    reopenChat,
  };
};

